import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, X, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useFacilityData, useSlotConfiguration } from '../hooks/booking/useBookingContextSlices';
import { configurationsAPI } from '../services/api';

const ConfigIndServ = () => {
  const { facility, loading: facilityLoading } = useFacilityData();
  const { updateSlotConfiguration, slotConfiguration } = useSlotConfiguration();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get service data from navigation state (if coming from Configure Service)
  const serviceDataFromState = location.state?.serviceData || null;
  
  const [services, setServices] = useState([]);
  const [currentService, setCurrentService] = useState({
    name: serviceDataFromState?.name || '',
    serviceType: 'individual_sports',
    prices: {
      monthly: '',
      weekly: '',
      perday: '',
      hourly: ''
    }
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const durationOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'perday', label: 'Per Day' },
    { value: 'hourly', label: 'Hourly' }
  ];

  const validateService = () => {
    const newErrors = {};
    
    if (!currentService.name.trim()) {
      newErrors.name = 'Service name is required';
    }
    
    // Validate that at least one price is entered
    const hasPrice = Object.values(currentService.prices).some(
      price => price && parseFloat(price) > 0
    );
    
    if (!hasPrice) {
      newErrors.prices = 'At least one price is required';
    }
    
    // Validate individual prices if entered
    Object.keys(currentService.prices).forEach(duration => {
      const price = currentService.prices[duration];
      if (price && parseFloat(price) <= 0) {
        newErrors[`price_${duration}`] = 'Price must be greater than 0';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddService = () => {
    if (validateService()) {
      // Filter out empty prices and convert to numbers
      const prices = {};
      Object.entries(currentService.prices).forEach(([duration, price]) => {
        if (price && parseFloat(price) > 0) {
          prices[duration] = parseFloat(price);
        }
      });
      
      if (Object.keys(prices).length === 0) {
        setErrors({ prices: 'At least one price is required' });
        return;
      }
      
      // Create a single service with all price options
      const newService = {
        id: Date.now(),
        name: currentService.name,
        serviceType: currentService.serviceType,
        prices: prices // Store all prices in one object
      };
      
      setServices(prev => [...prev, newService]);
      
      // Reset form
      setCurrentService({
        name: '',
        serviceType: 'individual_sports',
        prices: {
          monthly: '',
          weekly: '',
          perday: '',
          hourly: ''
        }
      });
      setErrors({});
    }
  };

  const handleRemoveService = (id) => {
    setServices(prev => prev.filter(s => s.id !== id));
  };

  const handleSaveAll = async () => {
    if (services.length === 0) {
      setSaveError('Please add at least one service before saving.');
      setTimeout(() => setSaveError(null), 3000);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Convert to service configuration format - one service with all price options
      const serviceConfigs = services.map(service => {
        // Get all duration labels for description
        const durationLabels = Object.keys(service.prices).map(duration => {
          const option = durationOptions.find(opt => opt.value === duration);
          return option ? option.label : duration;
        });
        
        return {
          service: {
            name: service.name,
            type: 'individual_sports',
            category: 'individual_sports',
            description: `${service.name} - Available durations: ${durationLabels.join(', ')}`,
            prices: service.prices // Store all prices in the service object
          },
          priceSetup: {
            prices: service.prices // Store prices in priceSetup as well
          }
        };
      });

      console.log('💾 Saving individual service configurations:', {
        servicesCount: serviceConfigs.length,
        services: serviceConfigs
      });

      // Save each service configuration to database
      const savePromises = services.map(async (service) => {
        // Get service_id from facility services
        const facilityService = facility?.services?.find(s => s.name === service.name);
        if (!facilityService || !facilityService.id) {
          throw new Error(`Service "${service.name}" not found in facility. Please configure facility services first.`);
        }
        
        console.log(`💾 Saving prices for service ${facilityService.id} (${service.name}):`, service.prices);
        
        // Save prices to database
        const result = await configurationsAPI.saveIndividualSportsPrices(facilityService.id, service.prices);
        console.log(`✅ Saved prices for service ${facilityService.id}:`, result);
        return result;
      });
      
      await Promise.all(savePromises);
      
      // Also update local state for backward compatibility
      const existingServices = slotConfiguration?.services || [];
      const updatedServices = [...existingServices, ...serviceConfigs];
      
      const updatedConfig = {
        ...slotConfiguration,
        services: updatedServices
      };

      updateSlotConfiguration(updatedConfig);

      console.log('✅ Configuration saved to database successfully');
      
      setSaveSuccess(true);
      
      // Show success message and navigate after a short delay
      setTimeout(() => {
        setSaveSuccess(false);
        navigate('/superadmin/facilities');
      }, 1500);
    } catch (error) {
      console.error('❌ Error saving configuration:', error);
      setSaveError(error.message || 'Failed to save configuration. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-6 flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard/configure-service', {
              state: {
                serviceData: {
                  name: currentService.name || serviceDataFromState?.name || '',
                  category: 'individual_sports',
                  description: serviceDataFromState?.description || ''
                }
              }
            })}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            title="Back to Configure Service"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Configure Individual Sports Service</h1>
            <p className="text-gray-600">
              Add individual sports services with pricing and duration options
              {serviceDataFromState && (
                <span> for: <strong>{serviceDataFromState.name}</strong></span>
              )}
            </p>
          </div>
        </div>

        {/* Add Service Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Add Individual Service</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Service Name Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name (from dropdown) *
              </label>
              <select
                value={currentService.name}
                onChange={(e) => {
                  setCurrentService(prev => ({ ...prev, name: e.target.value }));
                  setErrors(prev => ({ ...prev, name: '' }));
                }}
                className="input-field w-full"
              >
                <option value="">Select a service...</option>
                {facility?.services?.map((service, index) => (
                  <option key={service.id || index} value={service.name}>
                    {service.name}
                  </option>
                ))}
              </select>
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
              {!facilityLoading && (!facility?.services || facility.services.length === 0) && (
                <p className="text-yellow-600 text-sm mt-1">
                  No services available. Please configure facility services first.
                </p>
              )}
            </div>

            {/* Service Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Type *
              </label>
              <select
                value={currentService.serviceType}
                onChange={(e) => {
                  setCurrentService(prev => ({ ...prev, serviceType: e.target.value }));
                }}
                className="input-field w-full"
                disabled
              >
                <option value="individual_sports">Individual Sports</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Service type is set to Individual Sports</p>
            </div>

            {/* Price Configuration Table */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Configuration (₹) *
              </label>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Price (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {durationOptions.map((option) => (
                      <tr key={option.value} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {option.label}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={currentService.prices[option.value] || ''}
                            onChange={(e) => {
                              setCurrentService(prev => ({
                                ...prev,
                                prices: {
                                  ...prev.prices,
                                  [option.value]: e.target.value
                                }
                              }));
                              setErrors(prev => ({
                                ...prev,
                                [`price_${option.value}`]: '',
                                prices: ''
                              }));
                            }}
                            className="input-field w-full"
                            placeholder="Enter price"
                          />
                          {errors[`price_${option.value}`] && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors[`price_${option.value}`]}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {errors.prices && (
                <p className="text-red-500 text-sm mt-2">{errors.prices}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Configure prices for one or more duration options. Leave empty to skip a duration.
              </p>
            </div>
          </div>

          {/* Add Service Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleAddService}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add a Service</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-right">
            You can add multiple services
          </p>
        </div>

        {/* Added Services List */}
        {services.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Added Services ({services.length})
            </h2>
            
            <div className="space-y-3">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="border rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <div>
                          <h3 className="font-semibold text-gray-900">{service.name}</h3>
                          <span className="text-sm text-gray-600">Type: Individual Sports</span>
                        </div>
                      </div>
                      {/* Display all configured prices */}
                      <div className="ml-8">
                        <p className="text-sm font-medium text-gray-700 mb-2">Price Options:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(service.prices).map(([duration, price]) => {
                            const option = durationOptions.find(opt => opt.value === duration);
                            return (
                              <div key={duration} className="bg-gray-50 rounded p-2">
                                <div className="text-xs text-gray-600">{option?.label || duration}</div>
                                <div className="text-sm font-semibold text-gray-900">₹{price}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveService(service.id)}
                      className="text-red-500 hover:text-red-700 p-2 ml-4"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Save All Button */}
            <div className="mt-6 space-y-3">
              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{saveError}</p>
                </div>
              )}
              {saveSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-600 text-sm flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {services.length} individual service(s) saved successfully! Redirecting...
                  </p>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={isSaving}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      <span>Save All Services</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {services.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600">No services added yet. Add your first service above.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ConfigIndServ;

